import { useQueryClient } from '@tanstack/react-query';
import {
	type ErrorComponentProps,
	DefaultGlobalNotFound,
	isRedirect,
	Navigate,
	useLocation,
	useRouter,
} from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { Button } from '@worldcoo/ui/components/button';
import { AlertCircle } from 'lucide-react';
import { useServerFn } from '@tanstack/react-start';
import { errorReportData, extractErrorInfo, logErrorFn } from '@/lib/data';
import { isClientFaultRpcError, shouldReportFrontendError } from '@/lib/frontend-error-filter';
import { useEphemeralStore } from '@/lib/app-store';
import { isUnauthorizedError, ngoLoginRedirectPath } from '@/lib/ngo-session';
import { recentBreadcrumbs } from '@/lib/session-actions';

export function DefaultCatchBoundary(props: ErrorComponentProps) {
	// `unknown` since react-router 1.170.33 (#8209); `'isNotFound' in x` throws on a primitive.
	if (typeof props.error === 'object' && props.error !== null && 'isNotFound' in props.error) {
		return <DefaultGlobalNotFound />;
	}

	if (isRedirect(props.error)) {
		return <RedirectHandler redirectError={props.error} />;
	}

	// An expired session is an expected auth transition, never reported; only NGO procedures throw UNAUTHORIZED.
	if (isUnauthorizedError(props.error)) {
		return <NgoLoginRedirect />;
	}

	return <CatchBoundary {...props} />;
}

function RedirectHandler(props: { redirectError: unknown }) {
	const router = useRouter();

	useEffect(() => {
		if (isRedirect(props.redirectError)) {
			router.navigate(props.redirectError.options).catch((err) => {
				console.error('Failed to navigate after redirect:', err);
			});
		}
	}, [router, props.redirectError]);

	return null;
}

function NgoLoginRedirect() {
	const { pathname } = useLocation();
	// First render only: a later pass would target `/login` itself (see routes/ngos/route.tsx).
	const [redirect] = useState(() => ngoLoginRedirectPath(pathname));
	return <Navigate to="/ngos/login" search={{ redirect }} replace />;
}

export const VERSION_SKEW_RELOAD_KEY = 'worldcoo:version-skew-reload';
export const VERSION_SKEW_RELOAD_COOLDOWN_MS = 30_000;

/**
 * During a version-skew reload the DOM is torn down under running SSR scripts, so errors then are benign
 * fallout; every frontend-error reporter checks this first.
 */
export function isVersionSkewReloadInProgress() {
	if (typeof window === 'undefined') return false;
	const lastReload = window.sessionStorage.getItem(VERSION_SKEW_RELOAD_KEY);
	return !!lastReload && Date.now() - parseInt(lastReload) < VERSION_SKEW_RELOAD_COOLDOWN_MS;
}

/** The cooldown stops a reload loop when sticky sessions route a client to a mismatched task mid-deploy. */
function canReloadForVersionSkew() {
	if (typeof window === 'undefined') return false;
	return !isVersionSkewReloadInProgress();
}

function CatchBoundary(props: ErrorComponentProps) {
	const queryClient = useQueryClient();
	const logError = useServerFn(logErrorFn);
	const { t } = useLingui();
	const clearCheckoutCache = useEphemeralStore((state) => state.clearCheckoutCache);
	const hasLoggedError = useRef(false);

	// The app's one unknown-to-message conversion, so a non-Error reads the same here as in telemetry.
	const errorMessage = extractErrorInfo(props.error).message;
	const errorName = props.error instanceof Error ? props.error.name : '';
	// Firefox, Chrome, Safari and bundlers each word a stale chunk differently; missing Chrome's got it ticketed (TEC-3415, TEC-3413).
	const isModuleLoadError =
		errorMessage.includes('error loading dynamically imported module') ||
		errorMessage.includes('Failed to fetch dynamically imported module') ||
		errorMessage.includes('Importing a module script failed') ||
		errorName === 'ChunkLoadError';
	const isStaleServerFnError =
		errorMessage.includes('Server function info not found') ||
		errorMessage.includes('Server function module not resolved') ||
		errorMessage.includes('Server function module export not resolved') ||
		errorMessage.includes('expected content-type header to be set');
	const isVersionSkewError = isModuleLoadError || isStaleServerFnError;
	const shouldReloadForVersionSkew = isVersionSkewError && canReloadForVersionSkew();

	console.error('DefaultCatchBoundary Error:', props.error);

	// A version skew: bust the in-memory cache and resync with disk.
	useEffect(() => {
		if (hasLoggedError.current) return;
		hasLoggedError.current = true;

		queryClient.clear();
		clearCheckoutCache();

		if (shouldReloadForVersionSkew) {
			window.sessionStorage.setItem(VERSION_SKEW_RELOAD_KEY, String(Date.now()));
			window.location.reload();
			return;
		}

		if (isVersionSkewError) return;

		// A deliberate FORBIDDEN is the caller's fault, judged on the object as in the global handlers.
		if (isClientFaultRpcError(props.error)) return;

		const errorInfo = extractErrorInfo(props.error);
		// All four arguments: omitting the optional `stack` and `cause` compiles and skips their gates, and a failed
		// chunk load shows only in the cause chain of a #520 (TEC-3740, TEC-3722).
		if (!shouldReportFrontendError(errorInfo.name, errorInfo.message, errorInfo.stack, errorInfo.cause)) return;
		logError({
			data: errorReportData('', errorInfo, { source: 'boundary', breadcrumbs: recentBreadcrumbs() }),
		}).catch((logErr) => {
			console.error('Failed to log error to backend:', logErr);
		});
	}, [queryClient, props.error, logError, clearCheckoutCache, shouldReloadForVersionSkew, isVersionSkewError]);

	if (shouldReloadForVersionSkew) return null;

	return (
		<div className="flex flex-col gap-4 text-center">
			<div className="bg-destructive/10 mx-auto flex size-12 items-center justify-center rounded-full">
				<AlertCircle className="text-destructive size-6" />
			</div>

			<div className="flex flex-col gap-2">
				<h2 className="text-xl font-semibold">{t`Something went wrong`}</h2>
				<p className="text-muted-foreground text-sm">
					{t`We're sorry, but something unexpected happened. Please try again or refresh the page.`}
				</p>
			</div>

			{import.meta.env.DEV && props.error instanceof Error && (
				<details className="text-left">
					<summary className="text-muted-foreground cursor-pointer text-sm font-medium">{t`Technical details`}</summary>
					<div className="bg-muted mt-2 rounded-md p-3">
						<p className="font-mono text-xs wrap-break-word">{props.error.message}</p>
						{props.error.stack && (
							<pre className="text-muted-foreground mt-2 max-h-40 overflow-auto font-mono text-xs">
								{props.error.stack}
							</pre>
						)}
					</div>
				</details>
			)}

			<div className="flex flex-col gap-2">
				<Button onClick={() => window.location.reload()} variant="default">
					{t`Try again`}
				</Button>
				<Button onClick={() => (window.location.href = '/')} variant="outline">
					{t`Go to homepage`}
				</Button>
			</div>
		</div>
	);
}
